/**
 * Find the implementation here: 👇🏻
 * https://docs.pmnd.rs/zustand/guides/nextjs#providing-the-store
 */

'use client';

import { type ReactNode, createContext, useRef, useContext } from 'react';
import { useStore as useZustandStore } from 'zustand';

import { type StoreType, createBoundStore } from '@/stores';

type StoreApi = ReturnType<typeof createBoundStore>;

export const StoreContext = createContext<StoreApi | undefined>(undefined);

interface StoreProviderProps {
  children: ReactNode;
}

export const StoreProvider = ({ children }: StoreProviderProps) => {
  const storeRef = useRef<StoreApi>();
  if (!storeRef.current) {
    storeRef.current = createBoundStore();
  }

  return <StoreContext.Provider value={storeRef.current}>{children}</StoreContext.Provider>;
};

export const useStore = <T,>(selector: (store: StoreType) => T): T => {
  const storeContext = useContext(StoreContext);

  if (!storeContext) {
    throw new Error(`useStore must be used within StoreProvider`);
  }

  return useZustandStore(storeContext, selector);
};
